import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatError } from "@/api";
import { toast } from "sonner";
import { Share2, Copy, QrCode, ExternalLink, MessageCircle, Download } from "lucide-react";

const SITE_ORIGIN = window.location.origin;

/**
 * Doctor's own "My Public Profile" card — shows their shareable link, a QR
 * code (downloadable for clinic display), and WhatsApp/copy share buttons.
 */
export default function MyPublicProfileCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/doctors/me/public-profile");
        setInfo(data);
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail));
      } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return null;
  if (!info) return null;
  const link = `${SITE_ORIGIN}${info.path}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const downloadQR = () => {
    const canvas = document.querySelector("[data-testid=my-profile-qr] canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `upchaar-${info.slug}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("QR downloaded");
  };

  return (
    <Card className="medical-card p-5" data-testid="my-public-profile-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shrink-0 shadow-md shadow-sky-500/30">
          <Share2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-semibold text-slate-900">My Public Profile</h3>
          <p className="text-sm text-slate-500 mt-0.5">Send patients a direct link to book — no app install needed.</p>

          <div className="mt-3 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-700"
               data-testid="my-profile-link">
            <span className="truncate flex-1">{link}</span>
            <a href={link} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-700 shrink-0" data-testid="my-profile-open">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={copy} data-testid="my-profile-copy" className="gap-1.5">
              <Copy className="w-3.5 h-3.5" /> Copy link
            </Button>
            <Button size="sm"
              data-testid="my-profile-whatsapp"
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Book an appointment: ${link}`)}`, "_blank")}
              className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5">
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </Button>
            <Button size="sm" variant="outline"
              data-testid="my-profile-qr-toggle"
              onClick={() => setShowQR((v) => !v)} className="gap-1.5">
              <QrCode className="w-3.5 h-3.5" /> {showQR ? "Hide QR" : "Show QR"}
            </Button>
          </div>

          {showQR && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-white border border-slate-200 rounded-lg" data-testid="my-profile-qr">
              <QRCodeCanvas value={link} size={140} level="M" includeMargin={true} />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Print this at your clinic</p>
                <p className="text-xs text-slate-500 mt-1">
                  Patients scan → land on your profile → book instantly.
                </p>
                <Button size="sm" variant="outline" onClick={downloadQR} className="mt-3 gap-1.5" data-testid="my-profile-qr-download">
                  <Download className="w-3.5 h-3.5" /> Download PNG
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
